import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Paper, TextField, Button, Grid, Avatar, CircularProgress, Alert, Box, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import SubscriptionStatus from '../components/SubscriptionStatus';

const profissoes = [
  'Pedreiro',
  'Ajudante de Pedreiro',
  'Doméstica',
  'Cuidador de Idoso',
  'Babá',
  'Capinador',
  'Bombeiro',
  'Eletricista',
  'Carpinteiro',
  'Serralheiro',
  'Montador de Moveis',
  'Segurança',
  'Garçon',
  'Entregador',
  'Professo Particular',
  'Pintor',
  'Programador',
  'Fotógrafo',
  'Personal Trainner',
  'Contador',
  'Gesseiro'
];

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    email: '',
    phone: '',
    profession: '',
    description: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [serviceImages, setServiceImages] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchProviderData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/providers/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const providerData = response.data;
        setFormData({
          name: providerData.name || '',
          cpf: providerData.cpf || '',
          email: providerData.email || '',
          phone: providerData.phone || '',
          profession: providerData.profession || '',
          description: providerData.description || '',
          street: providerData.address?.street || '',
          number: providerData.address?.number || '',
          neighborhood: providerData.address?.neighborhood || '',
          city: providerData.address?.city || '',
          state: providerData.address?.state || '',
        });

        if (providerData.profileImage) {
          setProfileImage(providerData.profileImage);
        }
        if (providerData.serviceImages) {
          setServiceImages(providerData.serviceImages);
        }

        setLoading(false);
      } catch (err) {
        setError('Erro ao carregar dados do perfil');
        setLoading(false);
        if (err.response?.status === 401) {
          logout();
          navigate('/login');
        }
      }
    };

    fetchProviderData();
  }, [user, navigate, logout]);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/subscriptions/current');
        setSubscription(response.data.subscription);
        setPlan(response.data.plan);
      } catch (err) {
        setError('Erro ao carregar informações da assinatura');
      }
    };

    fetchSubscription();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await convertToBase64(file);
        setProfileImage(base64);
      } catch (error) {
        setError('Erro ao processar a imagem');
      }
    }
  };

  const handleServiceImagesChange = async (e) => {
    const files = Array.from(e.target.files);
    if (serviceImages.length + files.length > 10) {
      setError('Máximo de 10 imagens permitido');
      return;
    }

    try {
      const base64Promises = files.map(file => convertToBase64(file));
      const base64Images = await Promise.all(base64Promises);
      setServiceImages([...serviceImages, ...base64Images]);
    } catch (error) {
      setError('Erro ao processar as imagens');
    }
  };

  const removeServiceImage = (index) => {
    setServiceImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token de autenticação não encontrado');
        setLoading(false);
        return;
      }

      const { street, number, neighborhood, city, state, ...restData } = formData;

      const data = {
        ...restData,
        address: {
          street,
          number,
          neighborhood,
          city,
          state
        },
        profileImage,
        serviceImages
      };

      // Garantir que todos os campos obrigatórios estejam presentes
      if (!data.cpf || !data.profession || !data.description || !data.address.street || !data.address.number || !data.address.neighborhood || !data.address.city || !data.address.state) {
        setError('Preencha todos os campos obrigatórios do perfil e endereço.');
        setLoading(false);
        return;
      }

      const response = await api.put('/providers/me', data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        setSuccess('Perfil atualizado com sucesso!');
        // Atualizar os dados do formulário com a resposta do servidor
        const providerData = response.data;
        setFormData({
          name: providerData.name || '',
          cpf: providerData.cpf || '',
          email: providerData.email || '',
          phone: providerData.phone || '',
          profession: providerData.profession || '',
          description: providerData.description || '',
          street: providerData.address?.street || '',
          number: providerData.address?.number || '',
          neighborhood: providerData.address?.neighborhood || '',
          city: providerData.address?.city || '',
          state: providerData.address?.state || '',
        });
        if (providerData.profileImage) {
          setProfileImage(providerData.profileImage);
        }
        if (providerData.serviceImages) {
          setServiceImages(providerData.serviceImages);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Erro ao atualizar:', err);
      setError(err.response?.data?.message || 'Erro ao atualizar perfil');
      setLoading(false);
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Painel do Profissional
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SubscriptionStatus subscription={subscription} plan={plan} />
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ marginTop: 4, padding: 4 }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Meu Painel Profissional
            </Typography>

            {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ marginBottom: 2 }}>{success}</Alert>}
            
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <Grid container spacing={3}>
                {/* Foto de Perfil */}
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar
                    src={profileImage}
                    alt={formData.name}
                    sx={{ width: 120, height: 120, mb: 2, border: '2px solid lightgray' }}
                  />
                  <Button variant="outlined" component="label">
                    Alterar Foto de Perfil
                    <input type="file" hidden accept="image/*" onChange={handleProfileImageChange} />
                  </Button>
                </Grid>

                {/* Dados Pessoais */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nome Completo"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="CPF"
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Telefone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Endereço */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Rua"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    required
                    fullWidth
                    label="Número"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    required
                    fullWidth
                    label="Bairro"
                    name="neighborhood"
                    value={formData.neighborhood}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Cidade"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Estado"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Profissão e Descrição */}
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Profissão</InputLabel>
                    <Select
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      label="Profissão"
                    >
                      {profissoes.map((prof) => (
                        <MenuItem key={prof} value={prof}>
                          {prof}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={4}
                    label="Descrição"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Imagens de Serviço */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Fotos dos Serviços (máximo 10)
                  </Typography>
                  <Grid container spacing={2}>
                    {serviceImages.map((image, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Box sx={{ position: 'relative' }}>
                          <img
                            src={image}
                            alt={`Serviço ${index + 1}`}
                            style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                          />
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            onClick={() => removeServiceImage(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                    {serviceImages.length < 10 && (
                      <Grid item xs={6} sm={4} md={3}>
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{ height: '150px', width: '100%' }}
                        >
                          Adicionar Foto
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            multiple
                            onChange={handleServiceImagesChange}
                          />
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {/* Botão de Salvar */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Salvar Alterações'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProviderDashboard; 